import { LivePollType } from '@livepolls/ui-components/src/enums/livepoll-type.enum';
import { ITeamSetting } from '@livepolls/ui-components/src/interfaces/team-setting.interface';
import { ITeam } from '@livepolls/ui-components/src/interfaces/team.interface';
import { WuCheckbox, WuToggle } from '@npm-questionpro/wick-ui-lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TEAM_NAMES } from 'src/constants/default-team-names.constants';
import { ILivePollSessionSetting } from 'src/models/livepoll-session-setting.interface';
import { TextInput } from 'src/wick-ui/input/TextInput';
import Modal from 'src/wick-ui/modal/Modal';
import styles from './start-livepoll-modal.module.css';
import { TeamSetting } from './team-setting';

interface Props {
  onDeploy(data: ILivePollSessionSetting): void;
  onCloseModal(): void;
  isLoading: boolean;
  error: Error | null;
  livePollType: LivePollType;
}

export const StartLivePollModal = ({
  onDeploy,
  onCloseModal,
  livePollType,
  isLoading,
  error,
}: Props) => {
  const { t } = useTranslation();
  const [sessionName, setSessionName] = useState<string>('');
  const [responseTimeLimit, setResponseTimeLimit] = useState<number>(0);
  const [enableAnonymousResponses, setEnableAnonymousResponses] =
    useState<boolean>(false);
  const [enableTeams, setEnableTeams] = useState<boolean>(false);
  const [enablePresenterNotes, setEnablePresenterNotes] =
    useState<boolean>(false);
  const [teamSize, setTeamSize] = useState<number>(0);
  const [manualAssignTeams, setManualAssignTeams] = useState<boolean>(false);
  const [teams, setTeams] = useState<ITeam[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const RESPONSE_TIME_LIMITS = [
    { time: 0, text: t('noLimit') },
    { time: 20, text: t('twentySeconds') },
    { time: 30, text: t('thirtySeconds') },
    { time: 60, text: t('oneMin') },
    { time: 300, text: t('fiveMin') },
  ];
  const handleSelectResponseTimeLimit = (selectedTimeLimit: number) => {
    setResponseTimeLimit(selectedTimeLimit);
  };

  const areTeamsValid = (): boolean => {
    setValidationErrors([]);
    const errors = [];

    teams.map((team, index) => {
      if (team.name.trim() === '') {
        team.name = DEFAULT_TEAM_NAMES[index];
      }
      return team;
    });

    // check if team names and colors are unique
    const nameSet = new Set<string>();
    const colorSet = new Set<string>();
    teams.forEach(team => {
      nameSet.add(team.name);
      colorSet.add(team.color);
    });

    if (nameSet.size !== teamSize) {
      errors.push(t('teamNameUniqueError'));
    }

    if (colorSet.size !== teamSize) {
      errors.push(t('teamColorUniqueError'));
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    const teamSetting: ITeamSetting = {
      enabled: enableTeams,
    };

    if (enableTeams) {
      if (!areTeamsValid()) {
        return;
      }
      teamSetting.teamCount = teamSize;
      teamSetting.manualAssign = manualAssignTeams;
      teamSetting.teams = [...teams];
    }

    onDeploy({
      name: sessionName,
      responseTimeLimitInSecs: responseTimeLimit,
      anonymousResponses: enableAnonymousResponses,
      displayDifficultyLevels: false,
      teamSetting,
      presenterNotes: enablePresenterNotes,
    });
  };

  const submitText = t('startLiveSession');
  const isQuiz = livePollType === LivePollType.QUIZ;

  const getStylesForTeams = () => {
    if (isQuiz) {
      return styles.quizModal;
    }
    return styles.modal;
  };

  const timeSelectorDiv = (index: number) => {
    const responseLimit = RESPONSE_TIME_LIMITS[index].time;
    const displayText = RESPONSE_TIME_LIMITS[index].text;
    return (
      <div
        className={`${styles.timerText} ${
          responseTimeLimit === responseLimit ? styles.activeTimer : ''
        }`}
        onClick={() => handleSelectResponseTimeLimit(responseLimit)}
      >
        {displayText}
      </div>
    );
  };

  let content = (
    <div className={getStylesForTeams()}>
      <div className={styles.sessionNameLabel}>{t('sessionName')}</div>
      <TextInput
        autoFocus={true}
        placeholder={t('sessionNamePlaceholder')}
        initialValue={sessionName}
        onChange={setSessionName}
        maxLength={60}
      />
      <div className={styles.sessionNameLength}>{sessionName.length}/60</div>

      <div className={styles.secondRow}>
        <div className={styles.questionTimerText}>{t('questionTimer')}</div>
        <div className={styles.timerBar}>
          {RESPONSE_TIME_LIMITS.map((_, index) => (
            <>
              {timeSelectorDiv(index)}
              {index < RESPONSE_TIME_LIMITS.length - 1 &&
                addDivider(responseTimeLimit, index + 1)}
            </>
          ))}
        </div>
      </div>
      <div className={styles.thirdRow}>
        <div className={styles.checkboxRow}>
          <WuCheckbox
            label={t('respondentAnonymity')}
            onChange={setEnableAnonymousResponses}
          />
        </div>
        <div className={styles.checkboxRow}>
          <WuCheckbox
            label={t('speakerNotes')}
            onChange={setEnablePresenterNotes}
          />
          <span className={styles.speakerNotesDesc}>
            ({t('speakerNotesDesc')})
          </span>
        </div>
      </div>

      {isQuiz && (
        <div className={styles.teamsContainer}>
          <div className={styles.enableTeam}>
            <WuToggle
              onChange={setEnableTeams}
              label={t('teams')}
              labelPosition="left"
            />
          </div>
          <div>
            {enableTeams && (
              <WuCheckbox
                disabled={enableAnonymousResponses}
                label={t('autoAssignRespondents')}
                onChange={setManualAssignTeams}
              />
            )}
          </div>
        </div>
      )}

      {enableTeams && (
        <TeamSetting
          teamSize={teamSize}
          teams={teams}
          manualAssignTeams={manualAssignTeams}
          anonymousResponses={enableAnonymousResponses}
          onChangeTeamSize={setTeamSize}
          onChangeManualAssign={setManualAssignTeams}
          onChangeTeams={setTeams}
        />
      )}

      {validationErrors.map((validationError, index) => (
        <p key={index} className={styles.validationError}>
          * {validationError}
        </p>
      ))}
    </div>
  );

  if (error) {
    content = (
      <div>Oops an error occurred starting new session. ({error?.message})</div>
    );
  }

  return (
    <Modal
      title={t('sessionSetup')}
      closePopup={onCloseModal}
      onSubmit={handleSubmit}
      submitText={submitText}
      loading={isLoading}
      popupWidth={656}
      displayCancelBtn={false}
    >
      {content}
    </Modal>
  );
};

const addDivider = (responseTimeLimit: number, dividerNumber: number) => {
  const responseLimits = [20, 30, 60, 300];
  if (
    (dividerNumber === 1 && responseTimeLimit <= responseLimits[0]) ||
    (dividerNumber === 2 &&
      responseTimeLimit >= responseLimits[0] &&
      responseTimeLimit <= responseLimits[1]) ||
    (dividerNumber === 3 &&
      responseTimeLimit >= responseLimits[1] &&
      responseTimeLimit <= responseLimits[2]) ||
    (dividerNumber === 4 &&
      responseTimeLimit >= responseLimits[2] &&
      responseTimeLimit <= responseLimits[3])
  ) {
    return null;
  }

  return <div className={styles.divider}></div>;
};
